import type { EditingProject, ExportRecord } from "../types/project";
import type { WaveformPyramid } from "../waveform/types";
import { removeOpfsFile } from "./opfs";

const DB_NAME = "voicecutter-web";
const DB_VERSION = 1;

type StoredProject = EditingProject & { sourceFile?: Blob };

export async function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains("projects"))
        database.createObjectStore("projects", { keyPath: "id" });
      if (!database.objectStoreNames.contains("waveforms"))
        database.createObjectStore("waveforms");
      if (!database.objectStoreNames.contains("exports"))
        database.createObjectStore("exports", { keyPath: "id" });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () =>
      reject(request.error ?? new Error("IndexedDBを開けませんでした"));
  });
}

export async function saveProject(
  project: EditingProject,
  sourceFile?: File,
): Promise<void> {
  const database = await openDatabase();
  try {
    const existing = sourceFile
      ? undefined
      : await getProjectRecord(database, project.id);
    const record: StoredProject = {
      ...project,
      sourceFile: sourceFile ?? existing?.sourceFile,
    };
    await requestToPromise(
      database
        .transaction("projects", "readwrite")
        .objectStore("projects")
        .put(record),
    );
  } finally {
    database.close();
  }
}

export async function loadMostRecentProject(): Promise<{
  project: EditingProject;
  file: File;
} | null> {
  const database = await openDatabase();
  try {
    const records = await requestToPromise(
      database
        .transaction("projects", "readonly")
        .objectStore("projects")
        .getAll() as IDBRequest<StoredProject[]>,
    );
    const now = Date.now();
    const record = records
      .filter((item) => item.sourceFile && item.sourceExpiresAt > now)
      .sort((a, b) => b.updatedAt - a.updatedAt)[0];
    if (!record?.sourceFile) return null;
    const { sourceFile, ...project } = record;
    const file = new File([sourceFile], project.fileName, {
      type: project.mimeType,
      lastModified: project.fileLastModified,
    });
    return { project, file };
  } finally {
    database.close();
  }
}

export async function deleteProject(id: string): Promise<void> {
  const database = await openDatabase();
  try {
    await requestToPromise(
      database
        .transaction("projects", "readwrite")
        .objectStore("projects")
        .delete(id),
    );
  } finally {
    database.close();
  }
}

export async function cleanExpiredProjects(now = Date.now()): Promise<number> {
  const database = await openDatabase();
  const expiredOutputs: string[] = [];
  try {
    const transaction = database.transaction(
      ["projects", "exports"],
      "readwrite",
    );
    const projectStore = transaction.objectStore("projects");
    const exportStore = transaction.objectStore("exports");
    const projects = await requestToPromise(
      projectStore.getAll() as IDBRequest<StoredProject[]>,
    );
    const exports = await requestToPromise(
      exportStore.getAll() as IDBRequest<ExportRecord[]>,
    );
    let removed = 0;
    for (const project of projects) {
      if (project.sourceExpiresAt <= now) {
        project.sourceFile = undefined;
        projectStore.put(project);
        removed += 1;
      }
    }
    for (const record of exports) {
      if (record.expiresAt <= now) {
        exportStore.delete(record.id);
        expiredOutputs.push(record.opfsName);
      }
    }
    await transactionComplete(transaction);
    await Promise.all(expiredOutputs.map((name) => removeOpfsFile(name)));
    return removed;
  } finally {
    database.close();
  }
}

export async function saveWaveform(
  key: string,
  pyramid: WaveformPyramid,
): Promise<void> {
  const database = await openDatabase();
  try {
    await requestToPromise(
      database
        .transaction("waveforms", "readwrite")
        .objectStore("waveforms")
        .put(pyramid, key),
    );
  } finally {
    database.close();
  }
}

export async function loadWaveform(
  key: string,
): Promise<WaveformPyramid | null> {
  const database = await openDatabase();
  try {
    return (
      (await requestToPromise(
        database
          .transaction("waveforms", "readonly")
          .objectStore("waveforms")
          .get(key) as IDBRequest<WaveformPyramid | undefined>,
      )) ?? null
    );
  } finally {
    database.close();
  }
}

export async function saveExportRecord(record: ExportRecord): Promise<void> {
  const database = await openDatabase();
  try {
    await requestToPromise(
      database
        .transaction("exports", "readwrite")
        .objectStore("exports")
        .put(record),
    );
  } finally {
    database.close();
  }
}

function getProjectRecord(
  database: IDBDatabase,
  id: string,
): Promise<StoredProject | undefined> {
  return requestToPromise(
    database
      .transaction("projects", "readonly")
      .objectStore("projects")
      .get(id),
  );
}

function requestToPromise<T = IDBValidKey>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () =>
      reject(request.error ?? new Error("IndexedDB操作に失敗しました"));
  });
}

function transactionComplete(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () =>
      reject(transaction.error ?? new Error("IndexedDB transaction failed"));
    transaction.onabort = () =>
      reject(transaction.error ?? new Error("IndexedDB transaction aborted"));
  });
}
